import { useEffect } from 'react';
import { createPlugin } from '@backstage/core-plugin-api';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  useShadowRoot,
} from '@backstage/plugin-techdocs-react';

const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 1.25;
const WHEEL_SPEED = 0.0015;

const THEME_VARS = [
  '--md-default-bg-color',
  '--md-default-fg-color',
  '--md-default-fg-color--light',
  '--md-default-fg-color--lightest',
];

const svgIcon = (...paths: string[]) =>
  `<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">${paths
    .map(d => `<path d="${d}"/>`)
    .join('')}</svg>`;

const MAGNIFIER =
  'M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z';

const ICONS = {
  expand: svgIcon(
    'M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z',
  ),
  zoomIn: svgIcon(MAGNIFIER, 'M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z'),
  zoomOut: svgIcon(`${MAGNIFIER}M7 9h5v1H7z`),
  reset: svgIcon(
    'M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z',
  ),
  close: svgIcon(
    'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
  ),
};

const TOOLBAR_CSS = `
.spin-mermaid-toolbar {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--md-default-fg-color--lightest, #ddd);
  border-radius: 6px;
  background: var(--md-default-bg-color, #fff);
}
.spin-mermaid-btn {
  display: inline-flex;
  align-items: center;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--md-default-fg-color--light, #666);
  cursor: pointer;
  line-height: 1;
  padding: 5px;
}
.spin-mermaid-btn:hover {
  color: var(--md-default-fg-color, #333);
  background: var(--md-default-fg-color--lightest, #eee);
}
`;

const SHADOW_CSS = `
.md-typeset div.mermaid {
  position: relative;
  display: flex;
  justify-content: center;
  cursor: zoom-in;
}
.md-typeset div.mermaid > svg {
  max-width: 100% !important;
  max-height: var(--spin-mermaid-max-height, 400px);
  height: auto;
}
.md-typeset div.mermaid .spin-mermaid-toolbar {
  opacity: 0;
  transition: opacity 0.15s;
}
.md-typeset div.mermaid:hover .spin-mermaid-toolbar,
.md-typeset div.mermaid:focus-within .spin-mermaid-toolbar {
  opacity: 1;
}
@media (hover: none) {
  .md-typeset div.mermaid .spin-mermaid-toolbar {
    opacity: 1;
  }
}
${TOOLBAR_CSS}
`;

const DIALOG_CSS = `
dialog.spin-mermaid-dialog {
  border: none;
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
  background: var(--md-default-bg-color, #fff);
}
dialog.spin-mermaid-dialog::backdrop {
  background: rgba(0, 0, 0, 0.5);
}
.spin-mermaid-shell {
  position: relative;
  width: 90vw;
  height: 85vh;
}
.spin-mermaid-viewport {
  position: absolute;
  inset: 0;
  overflow: auto;
  cursor: grab;
  scrollbar-width: none;
}
.spin-mermaid-viewport::-webkit-scrollbar {
  display: none;
}
.spin-mermaid-viewport.is-dragging {
  cursor: grabbing;
}
.spin-mermaid-wrap {
  width: 100%;
  height: 100%;
}
.spin-mermaid-wrap > svg {
  width: 100%;
  height: 100%;
  max-width: none !important;
  max-height: none !important;
}
${TOOLBAR_CSS}
`;

const addStyle = (parent: Node, css: string) => {
  const style = document.createElement('style');
  style.dataset.spinTechdocsMermaid = 'true';
  style.textContent = css;
  parent.appendChild(style);
  return style;
};

const makeButton = (icon: string, title: string, onClick: () => void) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'spin-mermaid-btn';
  button.title = title;
  button.innerHTML = icon;
  button.addEventListener('click', onClick);
  return button;
};

const openModal = (diagram: HTMLElement, svgMarkup: string) => {
  const dialog = document.createElement('dialog');
  dialog.className = 'spin-mermaid-dialog';
  const computed = getComputedStyle(diagram);
  THEME_VARS.forEach(name => {
    const value = computed.getPropertyValue(name);
    if (value) dialog.style.setProperty(name, value);
  });

  const shell = document.createElement('div');
  shell.className = 'spin-mermaid-shell';
  const viewport = document.createElement('div');
  viewport.className = 'spin-mermaid-viewport';
  const wrap = document.createElement('div');
  wrap.className = 'spin-mermaid-wrap';
  wrap.innerHTML = svgMarkup;
  viewport.appendChild(wrap);

  let zoom = 1;
  const zoomTo = (next: number) => {
    const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    if (clamped === zoom) return;
    const ratioX =
      (viewport.scrollLeft + viewport.clientWidth / 2) / wrap.offsetWidth;
    const ratioY =
      (viewport.scrollTop + viewport.clientHeight / 2) / wrap.offsetHeight;
    zoom = clamped;
    wrap.style.width = `${zoom * 100}%`;
    wrap.style.height = `${zoom * 100}%`;
    viewport.scrollLeft = ratioX * wrap.offsetWidth - viewport.clientWidth / 2;
    viewport.scrollTop = ratioY * wrap.offsetHeight - viewport.clientHeight / 2;
  };

  viewport.addEventListener(
    'wheel',
    e => {
      e.preventDefault();
      zoomTo(zoom * Math.exp(-e.deltaY * WHEEL_SPEED));
    },
    { passive: false },
  );

  let drag: {
    id: number;
    x: number;
    y: number;
    left: number;
    top: number;
  } | null = null;
  viewport.addEventListener('pointerdown', e => {
    if ((e.target as HTMLElement).closest('.spin-mermaid-toolbar')) return;
    e.preventDefault();
    drag = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      left: viewport.scrollLeft,
      top: viewport.scrollTop,
    };
    viewport.setPointerCapture(e.pointerId);
    viewport.classList.add('is-dragging');
  });
  viewport.addEventListener('pointermove', e => {
    if (!drag || e.pointerId !== drag.id) return;
    viewport.scrollLeft = drag.left - (e.clientX - drag.x);
    viewport.scrollTop = drag.top - (e.clientY - drag.y);
  });
  const endDrag = (e: PointerEvent) => {
    if (drag && e.pointerId === drag.id) {
      drag = null;
      viewport.classList.remove('is-dragging');
    }
  };
  viewport.addEventListener('pointerup', endDrag);
  viewport.addEventListener('pointercancel', endDrag);
  viewport.addEventListener('lostpointercapture', endDrag);

  const toolbar = document.createElement('div');
  toolbar.className = 'spin-mermaid-toolbar';
  toolbar.append(
    makeButton(ICONS.zoomOut, 'Alejar', () => zoomTo(zoom / ZOOM_STEP)),
    makeButton(ICONS.zoomIn, 'Acercar', () => zoomTo(zoom * ZOOM_STEP)),
    makeButton(ICONS.reset, 'Restablecer', () => zoomTo(1)),
    makeButton(ICONS.close, 'Cerrar', () => dialog.close()),
  );

  shell.append(viewport, toolbar);
  dialog.appendChild(shell);
  dialog.addEventListener('click', e => {
    if (e.target === dialog) dialog.close();
  });
  dialog.addEventListener('close', () => dialog.remove());
  document.body.appendChild(dialog);
  dialog.showModal();
};

const TechDocsMermaidViewerAddon = () => {
  const shadowRoot = useShadowRoot();

  useEffect(() => {
    if (!shadowRoot) return undefined;

    const head = () => shadowRoot.querySelector('head') ?? shadowRoot;
    const shadowStyle = addStyle(head(), SHADOW_CSS);
    const docStyle = addStyle(document.head, DIALOG_CSS);
    const abort = new AbortController();

    const sync = () => {
      if (!shadowStyle.isConnected) {
        head().appendChild(shadowStyle);
      }
      shadowRoot
        .querySelectorAll<HTMLElement>('div.mermaid')
        .forEach(diagram => {
          const svg = diagram.querySelector('svg');
          if (diagram.dataset.spinMermaid || !svg) return;
          diagram.dataset.spinMermaid = 'true';
          const markup = svg.outerHTML;
          const open = () => openModal(diagram, markup);
          diagram.addEventListener(
            'click',
            e => {
              if (!(e.target as HTMLElement).closest('.spin-mermaid-toolbar')) {
                open();
              }
            },
            { signal: abort.signal },
          );
          const toolbar = document.createElement('div');
          toolbar.className = 'spin-mermaid-toolbar';
          toolbar.appendChild(
            makeButton(ICONS.expand, 'Ampliar diagrama', open),
          );
          diagram.appendChild(toolbar);
        });
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(shadowRoot, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      abort.abort();
      shadowStyle.remove();
      docStyle.remove();
      shadowRoot
        .querySelectorAll<HTMLElement>('div.mermaid')
        .forEach(diagram => {
          delete diagram.dataset.spinMermaid;
          diagram.querySelector('.spin-mermaid-toolbar')?.remove();
        });
      document
        .querySelectorAll<HTMLDialogElement>('dialog.spin-mermaid-dialog')
        .forEach(dialog => dialog.close());
    };
  }, [shadowRoot]);

  return null;
};

const techdocsMermaidViewerPlugin = createPlugin({
  id: 'techdocs-mermaid-viewer',
});

export const TechDocsMermaidViewer = techdocsMermaidViewerPlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsMermaidViewer',
    location: TechDocsAddonLocations.Content,
    component: TechDocsMermaidViewerAddon,
  }),
);
